import { FAIcon as FontAwesomeIcon } from './FAIcon';
import * as React from 'react'
import './ActionRenderComponent.scss'
import {
    BarChartLoader,
    calculateWidgetPixelSize,
    ComponentRenderer,
    DefaultLoader,
    DonutChartLoader,
    eventDispatcher,
    GaugeLoader,
    HeatmapChartLoader,
    Loading,
    PortalContainer,
    useAlert,
    useLoadingFeedback,
    useToast
} from 'uxp/components'
import {
    generateFullUUID,
    hasProperty,
    hasValue,
    loadScriptFileAsync,
    replaceWithData,
    tryParseJSON
} from '../../../util'
import { IContextProvider, IWidgetPreloader } from '../../../uxp'
import { IAction, ILucyAction, IRedirectWidget } from '../action-editor/ActionEditor'
import { IWDDesignModeProps } from '../common'
import { Transition } from 'uxp/components';
import { IContextData } from '../../../hooks/useContextData'

interface IActionRenderComponentProps {
    designer: IWDDesignModeProps
    uxpContext: IContextProvider
    action: IAction
    contextData: IContextData,
    onClose: () => void,
    onReloadWidget: () => void,
    customParams?: any,
}


// clever hack? or ugly hack? 🤷‍♂ very clever hack 😁 ️
function parseObject(o: any, values: any): any {
    if (o == null) {
        return o;
    }
    if (typeof o == undefined) {
        return o;
    }
    if (o instanceof Date) {
        return o;
    }
    if (typeof o == 'boolean') {
        return o;
    }
    if (typeof o == 'number') {
        return o;
    }
    if (typeof o === 'string') {
        return replaceWithData(o, values);
    }

    if (o instanceof Array) {
        return o.map(x => parseObject(x, values));
    }

    let newObject: any = {};
    for (let k in o) {
        if (!o.hasOwnProperty(k)) {
            continue;
        }
        newObject[k] = parseObject(o[k], values);
    }
    return newObject;
}


const ActionRenderComponent: React.FC<IActionRenderComponentProps> = (props) => {

    if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) return null

    let [widget, setWidget] = React.useState<IRedirectWidget>(null)
    let [lucyAction, setLucyAction] = React.useState<ILucyAction>(null)

    let toast = useToast()

    React.useEffect(() => {
        if (props.action) {
            let { type, redirectUrl, redirectWidget, lucyAction, customEvent } = props.action

            if (type == 'open-url') {
                if (!redirectUrl) redirectUrl = { url: 'about:blank' }

                console.log(props, "Action Props")

                redirectUrl = parseObject(redirectUrl, props.contextData);
                let url = hasProperty(redirectUrl, '$.url') ? redirectUrl.url || 'about:blank' : 'about:blank'
                let target = hasProperty(redirectUrl, '$.target') ? redirectUrl.target || '_blank' : '_blank'
                window.open(url, target)
                if (props.onClose) props.onClose()
            }
            else if (type == 'open-widget') {
                if (!redirectWidget) redirectWidget = { widget: null, params: {} }
                redirectWidget = parseObject(redirectWidget, props.contextData);

                console.log('redirect widget ', redirectWidget);

                setWidget(redirectWidget)
            }
            else if (type == 'execute-action') {
                if (!lucyAction) lucyAction = { modelName: null, action: null, params: {} }
                lucyAction = parseObject(lucyAction, props.contextData);

                setLucyAction(lucyAction)
            }
            else if (type == 'trigger-custom-event' && !!customEvent && hasValue(customEvent?.eventId)) {
                eventDispatcher(`instance-${(new Date()).getTime()}`, customEvent.eventId, parseObject(customEvent.params || {}, props.contextData) )
            }
        }
        else {
            setWidget(null)
            setLucyAction(null)
        }

    }, [props])

    return <React.Fragment>

        <WidgetModal
            show={widget != null}
            onClose={() => { props.onClose() }}
            uxpContext={props.uxpContext}
            widget={widget}
        />

        <ExecuteActionModal
            show={lucyAction != null}
            onClose={() => { props.onClose() }}
            uxpContext={props.uxpContext}
            contextData={props.contextData}
            lucyAction={lucyAction}
            onReloadWidget={props.onReloadWidget}
            customParams={props.customParams}
        />

    </React.Fragment>
}


interface IWidgetInstanceProps {
    loading: boolean,
    widget: any,
    props: { [key: string]: any },
    component: any,
    // template envelope ({__template__, uiProps}) passed as ComponentRenderer
    // configuredProps — mirrors how dashboards seed template widgets
    configuredProps?: { [key: string]: any },
    layout?: {
        width: number,
        height: number
    }
}

interface IWidgetModalProps {
    uxpContext: IContextProvider,
    show: boolean,
    onClose: () => void,
    widget: IRedirectWidget
}
const WidgetModal: React.FunctionComponent<IWidgetModalProps> = (props) => {

    let toast = useToast()
    let [widget, setWidget] = React.useState<IWidgetInstanceProps>(null)
    let [loading, setLoading] = React.useState(true)
    let [styles, setStyles] = React.useState<React.CSSProperties>(null)
    let [loader, setLoader] = React.useState<IWidgetPreloader>('default')

    React.useEffect(() => {
        prepareWidget();
        // prepareWidget resolves the target component from props.widget.widget (a
        // serializable descriptor: id/name/sourceUrl/isTemplate/templateId) and
        // builds props from props.widget.params — both must be keyed or a changed
        // target widget left the modal showing the stale component.
        // Keyed by stringified CONTENT (not reference): the parent ActionRender
        // component re-clones redirectWidget via parseObject on every render, so a
        // reference dep here would refire the prepare on every parent render —
        // reintroducing the very refetch storm the narrow deps were guarding against.
    }, [JSON.stringify(props.widget?.params), JSON.stringify(props.widget?.widget)]);

    // (window as any).Last = props.widget;
    React.useEffect(() => {
        if (!styles || !widget || !widget.widget || widget.widget.loading) setLoading(true)
        else setTimeout(() => { setLoading(false) }, 500)
    }, [widget, styles])

    async function prepareWidget() {
        try {

            let redirectWidget = props.widget
            if (!redirectWidget) redirectWidget = { widget: null, params: {} }
            let widgetInstance = hasProperty(redirectWidget, '$.widget') ? redirectWidget.widget || null : null
            let params = hasProperty(redirectWidget, '$.params') ? redirectWidget?.params || {} : {}

            let widget: IWidgetInstanceProps = {
                loading: true,
                widget: widgetInstance,
                // additionalProps for ComponentRenderer (uxpContext is injected by it)
                props: {
                    instanceId: generateFullUUID(),
                    renderInModal: true,
                    ...params,
                    params: { ...params }
                },
                component: null,
                configuredProps: {}
            }

            if (widget?.widget && !widget?.component) {
                let { id, name, sourceUrl } = widget.widget
                console.log("__detaisl ", id, name);

                // find the widget in registerd widgets 
                let registeredWidgets = (window as any).Widgets
                let instance = registeredWidgets.find((r: any) => (r.id == id))
                if (!instance) {
                    await loadScriptFileAsync(sourceUrl)
                }
                registeredWidgets = (window as any).Widgets
                instance = registeredWidgets.find((r: any) => (r.id == id))

                if (!instance) {
                    toast.error("Unable to find the widget")
                    console.log("Widget not found even after loading the module");
                    return
                }

                setLoader(instance?.configs?.preLoader || 'default')

                // v5: window.Widgets entries are ComponentDefinitions (component under `.component`);
                // v4 used `.widget`. Support both so the target widget actually mounts (else blank modal).
                widget.component = instance.component || instance.widget
                widget.loading = false

                let layout = instance?.configs?.layout || {}

                if (widget.widget.isTemplate) {
                    // saved template — the whole {__template__, uiProps} envelope becomes
                    // configuredProps (same as dashboard tiles); layout from the envelope
                    let details = await props?.uxpContext?.executeService('UXP', 'CardTemplate:DetailsById', { 'Id': widget?.widget?.templateId }, { json: true })
                    if (details) {
                        let template = tryParseJSON(details[0].Template, {})
                        if (template && template.uiProps) {
                            widget.configuredProps = template
                        }
                        layout = template?.__template__?.layout || layout
                    }
                }

                // widget box: same responsive cell math as the dashboard grid
                let { width, height } = calculateWidgetPixelSize(document.body.clientWidth, layout)

                let _styles: React.CSSProperties = { width, height }
                if (height > document.body.clientHeight) {
                    _styles.top = 0
                    _styles.transform = `translateX(-50%)`
                }
                widget.props.__width__ = width
                widget.props.__height__ = height
                setStyles(_styles)
                setWidget(prev => ({ ...prev, ...widget }))
            }
        }
        catch (e) {
            console.log("exception in prepare ", e);
        }
    }




    // let styles: React.CSSProperties = { width: 100, height: 100 }
    // if (widget?.layout && widget?.layout?.width) styles.width = widget.layout.width + 'px'
    // if (widget?.layout && widget?.layout?.height) {
    //     styles.height = widget.layout.height + 'px'
    //     if (widget.layout.height > document.body.clientHeight) {
    //         styles.top = 0
    //         styles.transform = `translateX(-50%)`
    //     }
    // }

    function renderloader() {
        let Component = DefaultLoader

        switch (loader) {
            case 'donut-chart':
                Component = DonutChartLoader
                break;
            case 'line-chart':
            case 'bar-chart':
                Component = BarChartLoader
                break;
            case 'heatmap-chart':
                Component = HeatmapChartLoader
                break;
            case 'gauge':
                Component = GaugeLoader
                break;
        }

        return <Component />
    }

    return <Transition
        in={props.show && !!styles}
        duration={300}
        className={'m-zoom-fc'}
        unmountOnExit={true}
    >
        <PortalContainer>
            <div className="uxp_wd-render-modal" >

                <div className="modal-backdrop"
                    onClick={() => props.onClose()}></div>

                <div className="close-btn" onClick={() => props.onClose()} >
                    <FontAwesomeIcon icon={['fas', 'times']} />
                </div>
                <div className="uxp_wd-render-modal__box" style={styles}>
                    <div className="uxp_wd-render-modal__panel">

                        {loading && <div className='loader-container'>
                            {loader ? renderloader() : <Loading />}
                        </div>}
                        {!loading && widget?.component && <ComponentRenderer
                            moduleId={(widget.widget?.id || '').split('/')[0]}
                            componentId={(widget.widget?.id || '').split('/').slice(1).join('/')}
                            configuredProps={widget.configuredProps || {}}
                            hasConfigured={true}
                            additionalProps={widget.props || {}}
                            notFoundMessage="Unable to render the widget"
                        />}
                    </div>
                </div>
            </div>
        </PortalContainer>
    </Transition>
}



interface IExecuteActionModalProps {
    uxpContext: IContextProvider,
    show: boolean,
    onClose: () => void,
    lucyAction: ILucyAction,
    contextData: IContextData,
    customParams?: any,
    onReloadWidget: () => void
}

/**
 * Non-visual runner for the execute-action flow (v5). Shows the executing
 * phase via the LoadingFeedback overlay and the terminal state via themed
 * alerts (useAlert). The [props.show] gate is the lifecycle guard — the
 * parent re-clones the action object on every render, so keying on show
 * keeps a single execution per trigger (v4 parity).
 */
const ExecuteActionModal: React.FunctionComponent<IExecuteActionModalProps> = (props) => {

    const alerts = useAlert()
    const loading = useLoadingFeedback()

    React.useEffect(() => {
        if (props.show) executeAction(props.lucyAction)
    }, [props.show])

    async function executeAction(lucyAction: ILucyAction) {
        if (!lucyAction || !hasValue(lucyAction.modelName) || !hasValue(lucyAction.action)) return

        let { modelName, action, params, postExecution, dialogTitle, executionMsg } = lucyAction
        params = { ...params, ...props.customParams }

        loading.show({
            message: hasValue(dialogTitle) ? dialogTitle : 'Executing action',
            submessage: executionMsg || ''
        })

        try {
            await props.uxpContext.executeAction(modelName, action, params, { json: true })
            loading.hide()

            const success = postExecution?.success || ({} as any)
            await alerts.success({
                ...(hasValue(dialogTitle) ? { title: dialogTitle } : {}),
                content: success.msg || 'Executed successfully',
                closeButtonTitle: success.buttonTitle || 'Done'
            })
            if (success.reloadWidget) props.onReloadWidget()
        }
        catch (e) {
            loading.hide()

            const error = postExecution?.error || ({} as any)
            await alerts.error({
                ...(hasValue(dialogTitle) ? { title: dialogTitle } : {}),
                content: error.msg || 'Execution failed',
                closeButtonTitle: error.buttonTitle || 'Close'
            })
            if (error.reloadWidget) props.onReloadWidget()
        }
        finally {
            props.onClose()
        }
    }

    return null
}

export default ActionRenderComponent